import { useCountDown } from "ahooks";
import { motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
const FlipTexts = [ "SECS", "MINS", "HRS", "DAYS" ];

// ---------------------------- ------------------------------------------------
// Types & Constants
// ----------------------------------------------------------------------------

interface FlipUnitProps {
    value: number | string;
    label?: string;
}

const TRANSITION_DURATION = 0.6; // Seconds
const COMMON_TEXT_STYLE = "text-t1 text-[22px] font-black tracking-[0.5px] leading-none select-none";
// 字体样式基于现有的设计
const DIN_FONT = {
    fontFamily: "'OpenSauceOne', 'DIN Condensed', 'Arial Narrow', sans-serif",
    WebkitFontSmoothing: "antialiased" as const,
};

// ----------------------------------------------------------------------------
// Sub-components
// ----------------------------------------------------------------------------

function StaticCard( { value, type }: { value: string; type: "top" | "bottom" } ) {
    return (
        <div
            className={`absolute left-0 right-0 h-1/2 overflow-hidden bg-surface-muted flex justify-center ${
                type === "top" ? "top-0 items-end rounded-t" : "bottom-0 items-start rounded-b"
            }`}
        >
            <span
                className={COMMON_TEXT_STYLE}
                style={{ ...DIN_FONT, transform: type === "top" ? "translateY(50%)" : "translateY(-50%)" }}
            >
                {value}
            </span>
        </div>
    );
}

function FlipUnit( { value, label }: FlipUnitProps ) {
    const stringValue = String( value ).padStart( 2, "0" );

    // 我们追踪 “当前” 目标值和 “上一个” 显示值
    // 来驱动动画序列。
    const [ current, setCurrent ] = useState( stringValue );
    const [ previous, setPrevious ] = useState( stringValue );
    const [ isFlipping, setIsFlipping ] = useState( false );

    useEffect( () => {
        if ( stringValue !== current ) {
            setPrevious( current );
            setCurrent( stringValue );
            setIsFlipping( true );
        }
    }, [ stringValue, current ] );

    const isThreeDigits = stringValue.length >= 3;
    // 如果是3位数，宽度增加到 48px (约 1.5 倍) 以容纳显示的数字
    const widthClass = isThreeDigits ? "w-12" : "w-[34px]";

    return (
        <div className="flex flex-col gap-1 items-center">
            <div className={`relative ${widthClass} h-10 bg-surface-muted rounded`} style={{ perspective: "300px" }}>
                {/* 1. 静态层 (背景) */}
                {/* 上半部分: 显示新值 (当翻转片向下移动时揭示) */}
                <StaticCard value={current} type="top" />

                {/* 下半部分: 显示旧值 (初始可见，会被覆盖) */}
                {
                    /* 等待: 如果没有翻转，我们是否只在各处显示 'current'?
                    是的，如果是静态，显示 Current/Current。
                    如果是翻转中，底部静态层实际上是 'Previous' (等待被覆盖)。
                    顶部静态层是 'Current' (等待被揭示)。
                */
                }
                <StaticCard value={!isFlipping ? current : previous} type="bottom" />

                {/* 2. 翻转片 (动画) */}
                {/* 仅在需要动画过渡时渲染翻转片 */}
                {isFlipping && (
                    <motion.div
                        key={current}
                        initial={{ rotateX: 0 }}
                        animate={{ rotateX: -180 }}
                        transition={{ duration: TRANSITION_DURATION, ease: "easeInOut" }}
                        onAnimationComplete={() => setIsFlipping( false )}
                        style={{ transformStyle: "preserve-3d", transformOrigin: "bottom", zIndex: 20 }}
                        className="absolute top-0 left-0 right-0 h-1/2"
                    >
                        {/* 正面: 旧值的上半部分 */}
                        <div
                            className="absolute inset-0 overflow-hidden bg-surface-muted flex items-end justify-center rounded-t"
                            style={{ backfaceVisibility: "hidden" }}
                        >
                            <span className={COMMON_TEXT_STYLE} style={{ ...DIN_FONT, transform: "translateY(50%)" }}>
                                {previous}
                            </span>
                        </div>

                        {/* 背面: 新值的下半部分 */}
                        <div
                            className="absolute inset-0 overflow-hidden bg-surface-muted flex items-start justify-center rounded-b"
                            style={{
                                backfaceVisibility: "hidden",
                                transform: "rotateX(180deg)", // 相对卡片旋转 180 度，所以当卡片处于 -180 度时它是正立的
                            }}
                        >
                            <span className={COMMON_TEXT_STYLE} style={{ ...DIN_FONT, transform: "translateY(-50%)" }}>
                                {current}
                            </span>
                        </div>
                    </motion.div>
                )}

                {/* 3. 分割线 & 阴影 */}
                {/* 轴线 */}
                <div
                    className="absolute top-1/2 left-0 right-0 h-px bg-surface-muted z-30 opacity-30"
                    style={{ transform: "translateY(-50%)" }}
                />

                {/* 微妙的内阴影以增加深度感 */}
                <div className="absolute inset-0 rounded pointer-events-none z-40" />
            </div>
            {label && <div className="text-t3 text-[8px] font-medium">{label}</div>}
        </div>
    );
}

// ----------------------------------------------------------------------------
// Main Component
// ----------------------------------------------------------------------------

interface CountdownFlipTimerProps {
    targetDate: number | string | Date;
    onEnd?: () => void;
}

export function CountdownFlipTimer( { targetDate, onEnd }: CountdownFlipTimerProps ) {
    const [ _, formattedRes ] = useCountDown( { targetDate, onEnd } );

    const { days, hours, minutes, seconds } = formattedRes;

    const totalHours = useMemo( () => {
        return days * 24 + hours;
    }, [ days, hours ] );

    // 检查计时器是否有效/运行中
    const isValid = totalHours > 0 || minutes > 0 || seconds > 0;

    // 分隔符组件
    const Separator = () => (
        <div className="flex flex-col gap-1 items-center">
            <div className="h-10 flex items-center">
                <span className="text-t1 font-bold text-sm pb-1">:</span>
            </div>
            {/* 占位，保持对齐 */}
            <div className="text-transparent text-[8px] font-medium">.</div>
        </div>
    );

    // 如果结束则渲染 空
    if ( !isValid ) {
        return null;
    }

    // 逻辑：
    // 1. 如果 > 1天: Days, Hrs, Mins (不显示 Secs)
    // 2. 如果 < 1天 但 >= 1小时: Hrs, Mins, Secs
    // 3. 如果 < 1小时: Mins, Secs

    if ( days >= 1 ) {
        return (
            <div className="flex items-center gap-1">
                <FlipUnit value={days} label={FlipTexts[3]} />
                <Separator />
                <FlipUnit value={hours} label={FlipTexts[2]} />
                <Separator />
                <FlipUnit value={minutes} label={FlipTexts[1]} />
            </div>
        );
    }

    if ( hours > 0 ) {
        return (
            <div className="flex items-center gap-1">
                <FlipUnit value={hours} label={FlipTexts[2]} />
                <Separator />
                <FlipUnit value={minutes} label={FlipTexts[1]} />
                <Separator />
                <FlipUnit value={seconds} label={FlipTexts[0]} />
            </div>
        );
    }

    return (
        <div className="flex items-center gap-1">
            <FlipUnit value={minutes} label={FlipTexts[1]} />
            <Separator />
            <FlipUnit value={seconds} label={FlipTexts[0]} />
        </div>
    );
}
